/**
 * Global assignee toast for in-app Task pings.
 *
 * Runs from App.vue (any post-login route) — not only Task Management —
 * so the assignee sees a sticky toast even when they are elsewhere in the app.
 * Gated on CASL `read` / `task_management` (same as Tasks nav + route).
 *
 * Cross-user: polls GET /pm/tasks (scope=mine) and looks for unread pings on
 * tasks assigned to the logged-in employee (`lastPingedAt` or a
 * `changeHistory` ping entry written by the manager). localStorage is only
 * used for per-viewer "seen" state — never as the source of the ping itself.
 *
 * Also syncs the navbar notification inbox: when /api/notifications is up,
 * missing ping/escalation rows are POSTed (bridge) and the list is re-fetched;
 * otherwise session synthesis is used. Toast "seen" and bell "read" are
 * independent — closing a toast does not clear the bell badge.
 *
 * Toast options: timeout: false (sticky), closeOnClick: true, custom close
 * via ToastificationContent's X. Marks the unread batch as seen after show
 * so refresh does not stack toasts; a later new ping can toast again.
 *
 * On 401/403 from GET /pm/tasks, polling stops until the next login /
 * in-app navigation (see {@link resumeTaskPingPollingAfterAuth}). After
 * repeated 5xx / transient failures, polling also backs off until the
 * next navigation resume (same hook).
 *
 * When `VUE_APP_FCM_PUSH_ONLY=true` (default), manager pings are not shown
 * via poll toasts or bell rows — only FCM / OS push. Escalations and
 * reminders still sync to the navbar inbox via this poller.
 */

import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import taskService from '@/services/task-service'
import { resetNotificationApiAuthGate } from '@/services/notification-service'
import store from '@/store'
import {
  taskId as getTaskId,
  taskAssigneeEmpId,
  taskOwnerEmpId,
} from '@/utils/task-config'
import { FCM_PUSH_ONLY } from '@/utils/fcm-push'
import {
  STORAGE_KEY,
  getUnreadPingsForAssignee,
  markTaskPingSeen,
  markEmployeePingSeen,
} from '@/utils/task-ping-store'
import { canAccessTaskManagement } from '@/utils/task-permissions'

/** Sticky toast options for assignee ping notifications. */
export const TASK_PING_TOAST_OPTIONS = {
  timeout: false,
  closeOnClick: true,
  closeButton: false,
  hideProgressBar: true,
  icon: false,
  id: 'task-ping-unread',
}

/** How often to re-fetch the assignee's tasks while the app is open. */
export const POLL_INTERVAL_MS = 20 * 1000

/** Stop background polling after this many consecutive server/transient failures. */
export const MAX_CONSECUTIVE_FETCH_FAILURES = 3

const FETCH_COOLDOWN_MS = 15 * 1000
let fetchInFlight = null
let lastFetchAt = 0
/** Prevents overlapping toast shows while an async fetch is in flight. */
let toastCheckInFlight = false
let pollTimerId = null
/** Vue root used to restart the interval after an auth pause. */
let pollVm = null
/**
 * When true, skip GET /pm/tasks polls until
 * {@link resumeTaskPingPollingAfterAuth} (login / navigation).
 */
let authPaused = false
/** When true, skip polls after repeated 5xx until navigation resume. */
let serverPaused = false
let consecutiveFetchFailures = 0

function readUserEmpId() {
  try {
    const userData = JSON.parse(localStorage.getItem('userData') || 'null')
    if (!userData) return null
    return userData.emp_id ?? userData.empId ?? null
  } catch (e) {
    return null
  }
}

function normalizeTasksResponse(res) {
  if (Array.isArray(res)) return res
  if (res && Array.isArray(res.data)) return res.data
  if (res && res.status === 'OK' && Array.isArray(res.data)) return res.data
  return []
}

function isUnauthorizedTasksResult(res) {
  if (!res) return false
  const code = res.httpStatus != null ? res.httpStatus : res.status
  return code === 401 || code === 403
}

function isTransientTasksFailure(res) {
  if (!res) return true
  if (res.status === 'SERVICE_UNAVAILABLE') return true
  const code = res.httpStatus != null
    ? res.httpStatus
    : (typeof res.status === 'number' ? res.status : null)
  return code != null && code >= 500
}

function recordTransientFetchFailure() {
  consecutiveFetchFailures += 1
  if (consecutiveFetchFailures >= MAX_CONSECUTIVE_FETCH_FAILURES && !serverPaused) {
    serverPaused = true
    stopTaskPingPolling()
  }
}

function clearTransientFetchFailures() {
  consecutiveFetchFailures = 0
}

/**
 * Pause task ping polling + notification API retries after auth rejection.
 * Cleared by {@link resumeTaskPingPollingAfterAuth}.
 */
function pauseForUnauthorized() {
  if (authPaused) return
  authPaused = true
  stopTaskPingPolling()
}

/**
 * Fetch tasks visible to this employee. Prefer scope=mine (assignedTo or
 * createdBy === empId). Inbox synthesis uses assignee + creator rows
 * (pings for assignees, escalations for both); toast logic still filters
 * to assignee-only via `getUnreadPingsForAssignee`.
 *
 * @returns {Promise<Array|null|'unauthorized'>}
 */
async function fetchAssigneeTasks(empId, { force = false } = {}) {
  if (authPaused) return 'unauthorized'
  if (serverPaused) return []
  const now = Date.now()
  if (fetchInFlight) return fetchInFlight
  if (!force && now - lastFetchAt < FETCH_COOLDOWN_MS) return null

  fetchInFlight = taskService.getTasks({
    scope: 'mine',
    empId,
  })
    .then(res => {
      lastFetchAt = Date.now()
      if (isUnauthorizedTasksResult(res)) {
        pauseForUnauthorized()
        return 'unauthorized'
      }
      if (isTransientTasksFailure(res)) {
        recordTransientFetchFailure()
        return []
      }
      clearTransientFetchFailures()
      const tasks = normalizeTasksResponse(res)
      const empKey = String(empId)
      return tasks.filter(t => {
        const assignee = taskAssigneeEmpId(t)
        const owner = taskOwnerEmpId(t)
        return (assignee != null && String(assignee) === empKey)
          || (owner != null && String(owner) === empKey)
      })
    })
    .catch(() => [])
    .finally(() => {
      fetchInFlight = null
    })

  return fetchInFlight
}

function buildToastTitle({ taskPings, employeePing }) {
  if (taskPings.length === 1) {
    const { task, ping } = taskPings[0]
    const who = ping.managerName || 'Your manager'
    return `${who} asked for an update on "${task.title || 'a task'}"`
  }
  if (taskPings.length > 1) {
    return `You have ${taskPings.length} task update requests from your manager`
  }
  if (employeePing) {
    const who = employeePing.managerName || 'Your manager'
    return `${who} pinged you — please review and update your tasks`
  }
  return null
}

function showStickyPingToast(vm, title) {
  if (!vm || typeof vm.$toast !== 'function' || !title) return
  vm.$toast(
    {
      component: ToastificationContent,
      props: {
        title,
        icon: 'BellIcon',
        variant: 'warning',
      },
    },
    { ...TASK_PING_TOAST_OPTIONS },
  )
}

function markBatchSeen(empId, { taskPings, employeePing }) {
  taskPings.forEach(({ task, ping }) => {
    markTaskPingSeen(getTaskId(task), {
      lastPingedAt: ping.lastPingedAt,
      pingCount: ping.pingCount,
      managerName: ping.managerName,
      managerEmpId: ping.managerEmpId,
      empId: ping.empId != null ? ping.empId : empId,
      taskTitle: ping.taskTitle || (task && task.title) || null,
      viewerEmpId: empId,
    }, empId)
  })
  if (employeePing) {
    markEmployeePingSeen(empId, { lastPingedAt: employeePing.lastPingedAt })
  }
}

/**
 * Check unread pings for the logged-in assignee and show one sticky toast
 * for the current unread batch (if any). Safe to call on mount, route
 * change, poll ticks, and `storage` events for {@link STORAGE_KEY}.
 *
 * @param {import('vue').default} vm — Vue instance with `$toast` / `$route`
 * @param {{ fetchTasks?: boolean, forceFetch?: boolean }} [options]
 */
export async function checkAndToastUnreadTaskPings(vm, options = {}) {
  const { fetchTasks = true, forceFetch = false } = options
  if (toastCheckInFlight) return
  if (!vm) return

  // Skip auth / full-layout routes (login, etc.)
  if (vm.$route && vm.$route.meta && vm.$route.meta.layout === 'full') return

  // Same ACL as Tasks nav/route — no poll toast/inbox sync without access.
  if (!canAccessTaskManagement()) return

  const empId = readUserEmpId()
  if (empId == null) return

  if (authPaused || serverPaused) return

  toastCheckInFlight = true
  try {
    let tasks = []
    let skipToast = false
    if (fetchTasks) {
      const fetched = await fetchAssigneeTasks(empId, { force: forceFetch })
      if (fetched === 'unauthorized') return
      if (Array.isArray(fetched)) {
        tasks = fetched
      } else if (fetched === null) {
        // Cooldown hit with no fresh fetch — skip toast this tick rather than
        // falling back to manager-local store (that path cannot notify B).
        // Still do not wipe / skip inbox if we somehow have tasks; without
        // tasks there is nothing to ingest.
        skipToast = true
      }
    }

    // Durable navbar inbox — escalations/reminders; task_ping rows omitted when FCM_PUSH_ONLY.
    if (!skipToast || tasks.length) {
      try {
        store.dispatch('notifications/ingestTaskPings', { empId, tasks })
      } catch (e) {
        // store may be unavailable during early boot; inbox still hydrates later
      }
    }

    // Push-only: no poll-based sticky toasts for manager pings (FCM handles delivery).
    if (FCM_PUSH_ONLY) return

    if (skipToast) return

    const unread = getUnreadPingsForAssignee(empId, tasks)
    if (!unread.taskPings.length && !unread.employeePing) return

    const title = buildToastTitle(unread)
    if (!title) return

    showStickyPingToast(vm, title)
    // Mark shown so refresh does not re-stack; toast stays until user closes it.
    // Does NOT mark the navbar notification as read.
    markBatchSeen(empId, unread)
  } finally {
    toastCheckInFlight = false
  }
}

/**
 * Start a short-interval poll so the employee sees a manager ping without
 * opening Task Management. Idempotent; pass the same Vue root each time.
 * No-ops while {@link authPaused} (use {@link resumeTaskPingPollingAfterAuth}).
 */
export function startTaskPingPolling(vm) {
  if (!canAccessTaskManagement()) {
    stopTaskPingPolling()
    return
  }
  if (authPaused || serverPaused) {
    pollVm = vm || pollVm
    return
  }
  if (pollTimerId != null) return
  pollVm = vm || pollVm
  checkAndToastUnreadTaskPings(pollVm, { forceFetch: true })
  pollTimerId = setInterval(() => {
    checkAndToastUnreadTaskPings(pollVm, { forceFetch: true })
  }, POLL_INTERVAL_MS)
}

export function stopTaskPingPolling() {
  if (pollTimerId != null) {
    clearInterval(pollTimerId)
    pollTimerId = null
  }
}

/**
 * After login or in-app navigation: if polling was paused for 401/403, clear
 * the gate (keeping soft-warn flags), retry once, and restart the interval.
 * When not paused, same as a normal toast check (existing App.vue behavior).
 * Call from App.vue `$route` watcher (skips full-layout / login).
 */
export function resumeTaskPingPollingAfterAuth(vm) {
  if (vm && vm.$route && vm.$route.meta && vm.$route.meta.layout === 'full') {
    return
  }
  if (!localStorage.getItem('userData') || !localStorage.getItem('accessToken')) {
    return
  }
  if (!canAccessTaskManagement()) {
    stopTaskPingPolling()
    return
  }
  pollVm = vm || pollVm
  const wasPaused = authPaused || serverPaused
  if (!wasPaused) {
    if (pollVm) checkAndToastUnreadTaskPings(pollVm, { forceFetch: true })
    return
  }
  authPaused = false
  serverPaused = false
  clearTransientFetchFailures()
  resetNotificationApiAuthGate({ keepWarnFlags: true })
  stopTaskPingPolling()
  startTaskPingPolling(pollVm)
}

export { STORAGE_KEY }

export default {
  TASK_PING_TOAST_OPTIONS,
  POLL_INTERVAL_MS,
  MAX_CONSECUTIVE_FETCH_FAILURES,
  STORAGE_KEY,
  checkAndToastUnreadTaskPings,
  startTaskPingPolling,
  stopTaskPingPolling,
  resumeTaskPingPollingAfterAuth,
}
